import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  TrendingUp,
  TrendingDown,
  Users,
  BarChart3,
  Sparkles,
  RefreshCw,
  Tag,
  ArrowUpRight,
  ArrowDownRight,
  Minus,
  FlaskConical,
  ExternalLink,
  ChevronDown,
  ChevronUp,
  Loader2,
} from "lucide-react";
import {
  ComposedChart, Line, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip as RechartsTooltip, ResponsiveContainer, ReferenceLine,
  ScatterChart, Scatter, Cell, Legend, Area,
} from "recharts";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Skeleton } from "@/components/ui/skeleton";
import { API, formatNumber, PLATFORMS } from "./constants";
import { useAuth } from "@/contexts/AuthContext";

const T = {
  ko: {
    title: "Post-Follower 상관관계 분석",
    beta: "Beta",
    betaDesc: "관리자가 공개한 기능입니다. 데이터가 축적될수록 분석 정확도가 높아집니다.",
    impactTab: "영향력 점수",
    timelineTab: "타임라인",
    categoryTab: "카테고리 분석",
    aiTab: "AI 요약",
    correlationTab: "상관관계",
    postTitle: "게시물",
    platform: "플랫폼",
    impactScore: "영향력 점수",
    followerBefore: "발행 전 팔로워",
    followerAfter: "발행 후 팔로워",
    actualGrowth: "실제 성장",
    expectedGrowth: "예상 성장",
    conversionRate: "전환율",
    conversionDesc: "조회수 1,000당 팔로워 증가",
    attribution: "기여 비중",
    groupSize: "그룹",
    solo: "단독",
    windowDays: "분석 기간 (일)",
    totalAnalyzed: "분석된 게시물",
    positiveImpact: "긍정적 영향",
    negativeImpact: "부정적 영향",
    avgImpact: "평균 영향력",
    selectAccount: "계정 선택",
    allPlatforms: "전체 플랫폼",
    followers: "팔로워",
    postMarker: "게시물 발행",
    noData: "분석 가능한 데이터가 부족합니다.",
    noDataDesc: "팔로워 스냅샷과 게시물 데이터가 축적되면 분석이 시작됩니다.",
    categorize: "AI 자동 분류",
    categorizing: "분류 중...",
    categorizeDesc: "게시물 제목을 기반으로 AI가 콘텐츠 유형을 자동 분류합니다.",
    categoryName: "카테고리",
    postCount: "게시물 수",
    avgViews: "평균 조회수",
    avgLikes: "평균 좋아요",
    avgComments: "평균 댓글",
    avgER: "평균 참여율",
    needsCategorization: "분류되지 않은 게시물이 있습니다.",
    generateSummary: "AI 분석 생성",
    generating: "분석 생성 중...",
    cached: "캐시됨",
    allCategories: "전체 카테고리",
    loading: "데이터 로딩 중...",
    viewsCorr: "조회수 상관관계",
    likesCorr: "좋아요 상관관계",
    commentsCorr: "댓글 상관관계",
    erCorr: "참여율 상관관계",
    followerDelta: "팔로워 변화",
    scatterTitle: "인게이지먼트 vs 팔로워 변화",
    corrCoeff: "상관계수",
    corrStrong: "강한 상관관계",
    corrModerate: "보통 상관관계",
    corrWeak: "약한 상관관계",
    corrNone: "상관관계 없음",
  },
  en: {
    title: "Post-Follower Correlation Analysis",
    beta: "Beta",
    betaDesc: "Accuracy improves as more data accumulates.",
    impactTab: "Impact Score",
    timelineTab: "Timeline",
    categoryTab: "Category Analysis",
    aiTab: "AI Summary",
    correlationTab: "Correlation",
    postTitle: "Post",
    platform: "Platform",
    impactScore: "Impact Score",
    followerBefore: "Followers Before",
    followerAfter: "Followers After",
    actualGrowth: "Actual Growth",
    expectedGrowth: "Expected Growth",
    conversionRate: "Conversion Rate",
    conversionDesc: "Follower gain per 1K views",
    attribution: "Attribution",
    groupSize: "Group",
    solo: "Solo",
    windowDays: "Window (days)",
    totalAnalyzed: "Posts Analyzed",
    positiveImpact: "Positive Impact",
    negativeImpact: "Negative Impact",
    avgImpact: "Avg Impact",
    selectAccount: "Select Account",
    allPlatforms: "All Platforms",
    followers: "Followers",
    postMarker: "Post Published",
    noData: "Insufficient data for analysis.",
    noDataDesc: "Analysis begins once follower snapshots and post data accumulate.",
    categorize: "AI Auto-Categorize",
    categorizing: "Categorizing...",
    categorizeDesc: "AI classifies content types based on post titles.",
    categoryName: "Category",
    postCount: "Posts",
    avgViews: "Avg Views",
    avgLikes: "Avg Likes",
    avgComments: "Avg Comments",
    avgER: "Avg ER",
    needsCategorization: "Some posts are not categorized yet.",
    generateSummary: "Generate AI Analysis",
    generating: "Generating...",
    cached: "Cached",
    allCategories: "All Categories",
    loading: "Loading data...",
    viewsCorr: "Views Correlation",
    likesCorr: "Likes Correlation",
    commentsCorr: "Comments Correlation",
    erCorr: "ER Correlation",
    followerDelta: "Follower Delta",
    scatterTitle: "Engagement vs Follower Change",
    corrCoeff: "Coefficient",
    corrStrong: "Strong",
    corrModerate: "Moderate",
    corrWeak: "Weak",
    corrNone: "None",
  },
  zh: {
    title: "帖子-粉丝相关性分析",
    beta: "Beta",
    betaDesc: "数据积累越多，分析精度越高。",
    impactTab: "影响力评分",
    timelineTab: "时间线",
    categoryTab: "分类分析",
    aiTab: "AI 摘要",
    correlationTab: "相关性",
    postTitle: "帖子",
    platform: "平台",
    impactScore: "影响力评分",
    followerBefore: "发布前粉丝",
    followerAfter: "发布后粉丝",
    actualGrowth: "实际增长",
    expectedGrowth: "预期增长",
    conversionRate: "转化率",
    conversionDesc: "每千次浏览的粉丝增长",
    attribution: "贡献比",
    groupSize: "分组",
    solo: "单独",
    windowDays: "分析窗口（天）",
    totalAnalyzed: "已分析帖子",
    positiveImpact: "正面影响",
    negativeImpact: "负面影响",
    avgImpact: "平均影响",
    selectAccount: "选择账户",
    allPlatforms: "所有平台",
    followers: "粉丝",
    postMarker: "帖子发布",
    noData: "分析数据不足。",
    noDataDesc: "粉丝快照和帖子数据积累后将开始分析。",
    categorize: "AI 自动分类",
    categorizing: "分类中...",
    categorizeDesc: "AI 根据帖子标题自动分类内容类型。",
    categoryName: "分类",
    postCount: "帖子数",
    avgViews: "平均浏览量",
    avgLikes: "平均点赞",
    avgComments: "平均评论",
    avgER: "平均参与率",
    needsCategorization: "部分帖子尚未分类。",
    generateSummary: "生成 AI 分析",
    generating: "生成中...",
    cached: "缓存",
    allCategories: "所有分类",
    loading: "数据加载中...",
    viewsCorr: "浏览量相关性",
    likesCorr: "点赞相关性",
    commentsCorr: "评论相关性",
    erCorr: "参与率相关性",
    followerDelta: "粉丝变化",
    scatterTitle: "互动 vs 粉丝变化",
    corrCoeff: "相关系数",
    corrStrong: "强相关",
    corrModerate: "中等相关",
    corrWeak: "弱相关",
    corrNone: "无相关",
  },
};

const getPlatformColor = (platform) => {
  const p = PLATFORMS.find((pl) => pl.id === platform);
  return p?.color || "#6b7280";
};

const ImpactBadge = ({ score }) => {
  if (score > 50) return <Badge className="bg-emerald-500/15 text-emerald-600 border-emerald-500/30 text-xs"><ArrowUpRight className="h-3 w-3 mr-0.5" />{score > 0 ? "+" : ""}{score.toLocaleString()}</Badge>;
  if (score > 0) return <Badge className="bg-green-500/10 text-green-600 border-green-500/20 text-xs"><ArrowUpRight className="h-3 w-3 mr-0.5" />+{score.toLocaleString()}</Badge>;
  if (score < -50) return <Badge className="bg-red-500/15 text-red-600 border-red-500/30 text-xs"><ArrowDownRight className="h-3 w-3 mr-0.5" />{score.toLocaleString()}</Badge>;
  if (score < 0) return <Badge className="bg-orange-500/10 text-orange-600 border-orange-500/20 text-xs"><ArrowDownRight className="h-3 w-3 mr-0.5" />{score.toLocaleString()}</Badge>;
  return <Badge variant="outline" className="text-xs"><Minus className="h-3 w-3 mr-0.5" />0</Badge>;
};

const CorrLabel = ({ value, t }) => {
  const abs = Math.abs(value);
  if (abs >= 0.7) return <span className="text-emerald-600 font-medium">{t.corrStrong} ({value})</span>;
  if (abs >= 0.4) return <span className="text-amber-600 font-medium">{t.corrModerate} ({value})</span>;
  if (abs >= 0.2) return <span className="text-muted-foreground">{t.corrWeak} ({value})</span>;
  return <span className="text-muted-foreground/60">{t.corrNone} ({value})</span>;
};

// ====== Impact Score Tab ======
const ImpactScoreTab = ({ lang, accounts }) => {
  const t = T[lang] || T.ko;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [platform, setPlatform] = useState("");
  const [accountId, setAccountId] = useState("");
  const [windowDays, setWindowDays] = useState("7");
  const [sortField, setSortField] = useState("impact");
  const [sortDir, setSortDir] = useState("desc");

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = { window_days: windowDays };
      if (platform) params.platform = platform;
      if (accountId) params.account_id = accountId;
      const { data: res } = await axios.get(`${API}/correlation/post-impact`, { params });
      setData(res);
    } catch (e) {
      if (e.response?.status !== 403) toast.error("Failed to load impact data");
    } finally {
      setLoading(false);
    }
  }, [platform, accountId, windowDays]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const sorted = (data?.posts || []).slice().sort((a, b) => {
    const dir = sortDir === "desc" ? -1 : 1;
    if (sortField === "impact") return dir * (a.follower_impact.impact_score - b.follower_impact.impact_score);
    if (sortField === "conversion") return dir * (a.conversion_rate - b.conversion_rate);
    if (sortField === "views") return dir * (a.metrics.views - b.metrics.views);
    if (sortField === "likes") return dir * (a.metrics.likes - b.metrics.likes);
    return 0;
  });

  const toggleSort = (field) => {
    if (sortField === field) setSortDir(d => d === "desc" ? "asc" : "desc");
    else { setSortField(field); setSortDir("desc"); }
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field) return null;
    return sortDir === "desc" ? <ChevronDown className="h-3 w-3 inline ml-0.5" /> : <ChevronUp className="h-3 w-3 inline ml-0.5" />;
  };

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3" data-testid="impact-filters">
        <Select value={platform || "all"} onValueChange={(v) => setPlatform(v === "all" ? "" : v)}>
          <SelectTrigger className="w-[160px] h-9" data-testid="impact-platform-select">
            <SelectValue placeholder={t.allPlatforms} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t.allPlatforms}</SelectItem>
            {PLATFORMS.map(p => <SelectItem key={p.id} value={p.id}>{p.name[lang] || p.name.en}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={accountId || "all"} onValueChange={(v) => setAccountId(v === "all" ? "" : v)}>
          <SelectTrigger className="w-[200px] h-9" data-testid="impact-account-select">
            <SelectValue placeholder={t.selectAccount} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t.selectAccount}</SelectItem>
            {accounts.map(a => <SelectItem key={a.id} value={a.id}>{a.display_name || a.username} ({a.platform})</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={windowDays} onValueChange={setWindowDays}>
          <SelectTrigger className="w-[120px] h-9" data-testid="impact-window-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {["3", "7", "14", "30"].map(d => <SelectItem key={d} value={d}>{d} {t.windowDays}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {/* Summary Cards */}
      {data?.summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Card><CardContent className="p-4">
            <p className="text-xs text-muted-foreground">{t.totalAnalyzed}</p>
            <p className="text-2xl font-bold mt-1" data-testid="total-analyzed">{data.summary.total_analyzed}</p>
          </CardContent></Card>
          <Card><CardContent className="p-4">
            <p className="text-xs text-muted-foreground">{t.positiveImpact}</p>
            <p className="text-2xl font-bold mt-1 text-emerald-600" data-testid="positive-count">{data.summary.positive_impact_count}</p>
          </CardContent></Card>
          <Card><CardContent className="p-4">
            <p className="text-xs text-muted-foreground">{t.negativeImpact}</p>
            <p className="text-2xl font-bold mt-1 text-red-500" data-testid="negative-count">{data.summary.negative_impact_count}</p>
          </CardContent></Card>
          <Card><CardContent className="p-4">
            <p className="text-xs text-muted-foreground">{t.avgImpact}</p>
            <p className="text-2xl font-bold mt-1" data-testid="avg-impact">{data.summary.avg_impact_score > 0 ? "+" : ""}{data.summary.avg_impact_score}</p>
          </CardContent></Card>
        </div>
      )}

      {/* Table */}
      {loading ? (
        <div className="space-y-2">{[...Array(5)].map((_, i) => <Skeleton key={i} className="h-12 w-full" />)}</div>
      ) : sorted.length === 0 ? (
        <Card><CardContent className="py-12 text-center">
          <BarChart3 className="h-10 w-10 mx-auto text-muted-foreground/40 mb-3" />
          <p className="text-muted-foreground font-medium">{t.noData}</p>
          <p className="text-sm text-muted-foreground/70 mt-1">{t.noDataDesc}</p>
        </CardContent></Card>
      ) : (
        <Card>
          <ScrollArea className="h-[500px]">
            <Table data-testid="impact-table">
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[280px]">{t.postTitle}</TableHead>
                  <TableHead>{t.platform}</TableHead>
                  <TableHead className="cursor-pointer select-none" onClick={() => toggleSort("likes")}>Likes <SortIcon field="likes" /></TableHead>
                  <TableHead className="cursor-pointer select-none" onClick={() => toggleSort("impact")}>{t.impactScore} <SortIcon field="impact" /></TableHead>
                  <TableHead>{t.attribution}</TableHead>
                  <TableHead>{t.actualGrowth}</TableHead>
                  <TableHead className="cursor-pointer select-none" onClick={() => toggleSort("conversion")}>{t.conversionRate} <SortIcon field="conversion" /></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sorted.map((row) => (
                  <TableRow key={row.post_id} data-testid={`impact-row-${row.post_id}`}>
                    <TableCell>
                      <div className="min-w-0">
                        <div className="min-w-0">
                          <p className="text-sm font-medium truncate max-w-[200px]">{row.title || "Untitled"}</p>
                          <p className="text-xs text-muted-foreground">{row.published_at?.slice(0, 10)}</p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" style={{ borderColor: getPlatformColor(row.platform), color: getPlatformColor(row.platform) }} className="text-xs">
                        {row.platform}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-sm">{formatNumber(row.metrics.likes)}</TableCell>
                    <TableCell><ImpactBadge score={row.follower_impact.impact_score} /></TableCell>
                    <TableCell>
                      {row.follower_impact.is_solo ? (
                        <span className="text-xs text-muted-foreground">{t.solo}</span>
                      ) : (
                        <div className="text-xs">
                          <span className="font-medium">{row.follower_impact.attribution_weight}%</span>
                          <span className="text-muted-foreground ml-1">({row.follower_impact.group_size})</span>
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="text-sm">
                      <span className={row.follower_impact.attributed_growth >= 0 ? "text-emerald-600" : "text-red-500"}>
                        {row.follower_impact.attributed_growth >= 0 ? "+" : ""}{Math.round(row.follower_impact.attributed_growth).toLocaleString()}
                      </span>
                    </TableCell>
                    <TableCell className="text-sm">{row.conversion_rate.toFixed(2)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </ScrollArea>
        </Card>
      )}
    </div>
  );
};

// ====== Timeline Tab ======
const TimelineTab = ({ lang, accounts }) => {
  const t = T[lang] || T.ko;
  const [accountId, setAccountId] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [days, setDays] = useState("30");

  const fetchTimeline = useCallback(async () => {
    if (!accountId) return;
    setLoading(true);
    try {
      const { data: res } = await axios.get(`${API}/correlation/timeline`, { params: { account_id: accountId, days } });
      setData(res);
    } catch (e) {
      if (e.response?.status !== 403) toast.error("Failed to load timeline");
    } finally {
      setLoading(false);
    }
  }, [accountId, days]);

  useEffect(() => { fetchTimeline(); }, [fetchTimeline]);

  const chartData = (data?.follower_timeline || []).map((s) => {
    const postsOnDate = (data?.post_markers || []).filter(p => p.date === s.date);
    return {
      date: s.date,
      followers: s.followers,
      postCount: postsOnDate.length,
      postTitles: postsOnDate.map(p => p.title).join(", "),
    };
  });

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <Select value={accountId} onValueChange={setAccountId}>
          <SelectTrigger className="w-[260px] h-9" data-testid="timeline-account-select">
            <SelectValue placeholder={t.selectAccount} />
          </SelectTrigger>
          <SelectContent>
            {accounts.map(a => <SelectItem key={a.id} value={a.id}>{a.display_name || a.username} ({a.platform})</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={days} onValueChange={setDays}>
          <SelectTrigger className="w-[100px] h-9">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {["7", "14", "30", "60", "90"].map(d => <SelectItem key={d} value={d}>{d}d</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {!accountId ? (
        <Card><CardContent className="py-12 text-center text-muted-foreground">{t.selectAccount}</CardContent></Card>
      ) : loading ? (
        <Card><CardContent className="py-12 flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></CardContent></Card>
      ) : !data?.data_available ? (
        <Card><CardContent className="py-12 text-center">
          <p className="text-muted-foreground">{t.noData}</p>
          <p className="text-sm text-muted-foreground/70 mt-1">{t.noDataDesc}</p>
        </CardContent></Card>
      ) : (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">{data.account?.display_name} - {t.followers} {t.timelineTab}</CardTitle>
            <CardDescription>{data.date_range?.start} ~ {data.date_range?.end}</CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={400} data-testid="timeline-chart">
              <ComposedChart data={chartData} margin={{ top: 10, right: 20, left: 10, bottom: 20 }}>
                <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
                <XAxis dataKey="date" tick={{ fontSize: 11 }} tickFormatter={(v) => v.slice(5)} />
                <YAxis yAxisId="followers" tick={{ fontSize: 11 }} tickFormatter={formatNumber} />
                <YAxis yAxisId="posts" orientation="right" tick={{ fontSize: 11 }} domain={[0, "auto"]} />
                <RechartsTooltip
                  content={({ active, payload }) => {
                    if (!active || !payload?.length) return null;
                    const d = payload[0]?.payload;
                    return (
                      <div className="bg-card border border-border rounded-lg p-3 shadow-lg text-sm">
                        <p className="font-medium">{d.date}</p>
                        <p className="text-primary">{t.followers}: {d.followers?.toLocaleString()}</p>
                        {d.postCount > 0 && (
                          <div className="mt-1 pt-1 border-t border-border/50">
                            <p className="text-amber-500 font-medium">{t.postMarker} ({d.postCount})</p>
                            <p className="text-xs text-muted-foreground truncate max-w-[200px]">{d.postTitles}</p>
                          </div>
                        )}
                      </div>
                    );
                  }}
                />
                <Area yAxisId="followers" type="monotone" dataKey="followers" stroke="hsl(var(--primary))" fill="hsl(var(--primary))" fillOpacity={0.08} strokeWidth={2} />
                {/* Post markers as bars */}
                <Bar yAxisId="posts" dataKey="postCount" fill="#f59e0b" opacity={0.7} barSize={6} radius={[4, 4, 0, 0]} />
              </ComposedChart>
            </ResponsiveContainer>
            {/* Post list */}
            {(data?.post_markers || []).length > 0 && (
              <div className="mt-4 space-y-2">
                <p className="text-sm font-medium text-muted-foreground">{t.postMarker} ({data.post_markers.length})</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {data.post_markers.map((p, i) => (
                    <div key={i} className="flex items-center gap-2 p-2 rounded-md bg-muted/30 text-sm">
                      <div className="min-w-0 flex-1">
                        <p className="truncate font-medium text-xs">{p.title}</p>
                        <p className="text-xs text-muted-foreground">{p.date} | {formatNumber(p.likes)} likes</p>
                      </div>
                      {p.post_url && <a href={p.post_url} target="_blank" rel="noreferrer"><ExternalLink className="h-3.5 w-3.5 text-muted-foreground" /></a>}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

// ====== Category Analysis Tab ======
const CategoryTab = ({ lang, accounts }) => {
  const t = T[lang] || T.ko;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [categorizing, setCategorizing] = useState(false);

  const fetchCategories = useCallback(async () => {
    setLoading(true);
    try {
      const { data: res } = await axios.get(`${API}/correlation/category-analysis`);
      setData(res);
    } catch (e) {
      if (e.response?.status !== 403) toast.error("Failed to load categories");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchCategories(); }, [fetchCategories]);

  const handleCategorize = async () => {
    setCategorizing(true);
    try {
      const { data: res } = await axios.post(`${API}/correlation/categorize-posts`);
      toast.success(res.message);
      fetchCategories();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Categorization failed");
    } finally {
      setCategorizing(false);
    }
  };

  const categoryColors = ["#3b82f6", "#10b981", "#f59e0b", "#ef4444", "#8b5cf6", "#ec4899", "#06b6d4", "#f97316", "#84cc16", "#6366f1", "#14b8a6"];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          {data?.needs_categorization !== false && data?.uncategorized_posts > 0 && (
            <p className="text-sm text-amber-600">{t.needsCategorization} ({data.uncategorized_posts})</p>
          )}
        </div>
        <Button onClick={handleCategorize} disabled={categorizing} size="sm" variant="outline" data-testid="categorize-btn">
          {categorizing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Tag className="h-4 w-4 mr-2" />}
          {categorizing ? t.categorizing : t.categorize}
        </Button>
      </div>
      <p className="text-xs text-muted-foreground">{t.categorizeDesc}</p>

      {loading ? (
        <div className="space-y-2">{[...Array(4)].map((_, i) => <Skeleton key={i} className="h-12 w-full" />)}</div>
      ) : (data?.categories || []).length === 0 ? (
        <Card><CardContent className="py-12 text-center">
          <Tag className="h-10 w-10 mx-auto text-muted-foreground/40 mb-3" />
          <p className="text-muted-foreground">{t.noData}</p>
          <p className="text-sm text-muted-foreground/60 mt-1">Click "{t.categorize}" to start</p>
        </CardContent></Card>
      ) : (
        <>
          {/* Bar chart */}
          <Card>
            <CardContent className="pt-6">
              <ResponsiveContainer width="100%" height={300}>
                <ComposedChart data={data.categories} layout="vertical" margin={{ left: 100, right: 20 }}>
                  <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
                  <XAxis type="number" tick={{ fontSize: 11 }} tickFormatter={formatNumber} />
                  <YAxis type="category" dataKey="category" tick={{ fontSize: 11 }} width={90} />
                  <RechartsTooltip formatter={(v) => formatNumber(v)} />
                  <Bar dataKey="metrics.avg_likes" name={t.avgLikes} barSize={16} radius={[0, 4, 4, 0]}>
                    {(data.categories || []).map((_, i) => (
                      <Cell key={i} fill={categoryColors[i % categoryColors.length]} fillOpacity={0.8} />
                    ))}
                  </Bar>
                </ComposedChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {/* Table */}
          <Card>
            <Table data-testid="category-table">
              <TableHeader>
                <TableRow>
                  <TableHead>{t.categoryName}</TableHead>
                  <TableHead className="text-right">{t.postCount}</TableHead>
                  <TableHead className="text-right">{t.avgViews}</TableHead>
                  <TableHead className="text-right">{t.avgLikes}</TableHead>
                  <TableHead className="text-right">{t.avgComments}</TableHead>
                  <TableHead className="text-right">{t.avgER}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.categories.map((c, i) => (
                  <TableRow key={c.category}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: categoryColors[i % categoryColors.length] }} />
                        <span className="font-medium text-sm">{c.category}</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-right text-sm">{c.post_count}</TableCell>
                    <TableCell className="text-right text-sm">{formatNumber(c.metrics.avg_views)}</TableCell>
                    <TableCell className="text-right text-sm">{formatNumber(c.metrics.avg_likes)}</TableCell>
                    <TableCell className="text-right text-sm">{formatNumber(c.metrics.avg_comments)}</TableCell>
                    <TableCell className="text-right text-sm">{c.metrics.avg_engagement_rate}%</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        </>
      )}
    </div>
  );
};

// ====== AI Summary Tab ======
const AISummaryTab = ({ lang, accounts }) => {
  const t = T[lang] || T.ko;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [platform, setPlatform] = useState("");
  const [accountId, setAccountId] = useState("");
  const [category, setCategory] = useState("");
  const [categories, setCategories] = useState([]);

  // Fetch available categories
  useEffect(() => {
    axios.get(`${API}/correlation/category-analysis`).then(({ data: res }) => {
      setCategories((res?.categories || []).map(c => c.category));
    }).catch(() => {});
  }, []);

  const generate = async () => {
    setLoading(true);
    try {
      const params = { language: lang };
      if (platform) params.platform = platform;
      if (accountId) params.account_id = accountId;
      if (category) params.category = category;
      const { data: res } = await axios.post(`${API}/correlation/ai-summary`, null, { params });
      setData(res);
    } catch (e) {
      toast.error(e.response?.data?.detail || "AI analysis failed");
    } finally {
      setLoading(false);
    }
  };

  const filteredAccounts = platform
    ? accounts.filter(a => a.platform === platform)
    : accounts;

  const sections = data?.sections || {};
  const hasData = Object.keys(sections).length > 0 && !data?.parse_error;

  const renderSection = (key, section) => {
    if (!section) return null;
    return (
      <Card key={key} className="overflow-hidden">
        <CardHeader className="pb-2">
          <CardTitle className="text-base">{section.title || key}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {section.content && <p className="text-sm text-muted-foreground whitespace-pre-wrap">{section.content}</p>}
          {section.items && (
            <div className="space-y-2">
              {section.items.map((item, i) => (
                <div key={i} className="p-3 rounded-md bg-muted/30 space-y-1">
                  {item.content_type && <Badge variant="outline" className="text-xs mb-1">{item.content_type}</Badge>}
                  {item.platform && <Badge variant="outline" className="text-xs mb-1">{item.platform}</Badge>}
                  {item.priority && <Badge className={`text-xs mb-1 ${item.priority === "high" ? "bg-red-500/10 text-red-600" : item.priority === "medium" ? "bg-amber-500/10 text-amber-600" : "bg-blue-500/10 text-blue-600"}`}>{item.priority}</Badge>}
                  {item.impact && <p className="text-sm">{item.impact}</p>}
                  {item.insight && <p className="text-sm">{item.insight}</p>}
                  {item.recommendation && <p className="text-sm text-primary">{item.recommendation}</p>}
                  {item.strategy && <p className="text-sm text-primary">{item.strategy}</p>}
                  {item.expected_impact && <p className="text-xs text-muted-foreground">{item.expected_impact}</p>}
                </div>
              ))}
            </div>
          )}
          {section.frequency && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
              <div className="p-3 rounded-md bg-muted/30"><p className="text-xs text-muted-foreground mb-1">Frequency</p><p>{section.frequency}</p></div>
              <div className="p-3 rounded-md bg-muted/30"><p className="text-xs text-muted-foreground mb-1">Timing</p><p>{section.timing}</p></div>
              <div className="p-3 rounded-md bg-muted/30"><p className="text-xs text-muted-foreground mb-1">Content Mix</p><p>{section.content_mix}</p></div>
            </div>
          )}
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3" data-testid="ai-summary-filters">
        <Select value={platform || "all"} onValueChange={(v) => { setPlatform(v === "all" ? "" : v); setAccountId(""); }}>
          <SelectTrigger className="w-[160px] h-9" data-testid="ai-platform-select">
            <SelectValue placeholder={t.allPlatforms} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t.allPlatforms}</SelectItem>
            {PLATFORMS.map(p => <SelectItem key={p.id} value={p.id}>{p.name[lang] || p.name.en}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={accountId || "all"} onValueChange={(v) => setAccountId(v === "all" ? "" : v)}>
          <SelectTrigger className="w-[200px] h-9" data-testid="ai-account-select">
            <SelectValue placeholder={t.selectAccount} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t.selectAccount}</SelectItem>
            {filteredAccounts.map(a => <SelectItem key={a.id} value={a.id}>{a.display_name || a.username} ({a.platform})</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={category || "all"} onValueChange={(v) => setCategory(v === "all" ? "" : v)}>
          <SelectTrigger className="w-[180px] h-9" data-testid="ai-category-select">
            <SelectValue placeholder={t.allCategories} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t.allCategories}</SelectItem>
            {categories.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          {data?.posts_analyzed ? <span>{data.posts_analyzed} posts analyzed</span> : null}
          {data?.cached ? <Badge variant="outline" className="text-xs">{t.cached}</Badge> : null}
          {(platform || accountId || category) && (
            <div className="flex items-center gap-1.5 ml-2">
              {platform && <Badge variant="secondary" className="text-xs">{platform}</Badge>}
              {accountId && <Badge variant="secondary" className="text-xs">{filteredAccounts.find(a => a.id === accountId)?.display_name || accountId}</Badge>}
              {category && <Badge variant="secondary" className="text-xs">{category}</Badge>}
            </div>
          )}
        </div>
        <Button onClick={generate} disabled={loading} size="sm" data-testid="ai-summary-btn">
          {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Sparkles className="h-4 w-4 mr-2" />}
          {loading ? t.generating : t.generateSummary}
        </Button>
      </div>

      {loading ? (
        <div className="space-y-4">{[...Array(3)].map((_, i) => <Skeleton key={i} className="h-40 w-full" />)}</div>
      ) : !hasData ? (
        <Card><CardContent className="py-12 text-center">
          <Sparkles className="h-10 w-10 mx-auto text-muted-foreground/40 mb-3" />
          <p className="text-muted-foreground">Click "{t.generateSummary}" to get AI-powered analysis</p>
        </CardContent></Card>
      ) : (
        <div className="space-y-4" data-testid="ai-summary-content">
          {renderSection("executive_summary", sections.executive_summary)}
          {renderSection("top_performing_content", sections.top_performing_content)}
          {renderSection("platform_insights", sections.platform_insights)}
          {renderSection("content_recommendations", sections.content_recommendations)}
          {renderSection("posting_strategy", sections.posting_strategy)}
        </div>
      )}
    </div>
  );
};

// ====== Correlation Tab ======
const CorrelationTab = ({ lang, accounts }) => {
  const t = T[lang] || T.ko;
  const [accountId, setAccountId] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [metric, setMetric] = useState("likes");

  const fetchCorrelation = useCallback(async () => {
    if (!accountId) return;
    setLoading(true);
    try {
      const { data: res } = await axios.get(`${API}/correlation/engagement-follower-correlation`, { params: { account_id: accountId } });
      setData(res);
    } catch (e) {
      if (e.response?.status !== 403) toast.error("Failed to load correlation data");
    } finally {
      setLoading(false);
    }
  }, [accountId]);

  useEffect(() => { fetchCorrelation(); }, [fetchCorrelation]);

  const scatterData = (data?.data_points || []).map(d => ({
    x: d[metric],
    y: d.follower_delta,
    title: d.title,
    category: d.category,
  }));

  const COLORS = { likes: "#ef4444", views: "#3b82f6", comments: "#10b981", engagement_rate: "#f59e0b" };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <Select value={accountId} onValueChange={setAccountId}>
          <SelectTrigger className="w-[260px] h-9" data-testid="corr-account-select">
            <SelectValue placeholder={t.selectAccount} />
          </SelectTrigger>
          <SelectContent>
            {accounts.map(a => <SelectItem key={a.id} value={a.id}>{a.display_name || a.username} ({a.platform})</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={metric} onValueChange={setMetric}>
          <SelectTrigger className="w-[140px] h-9">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="views">Views</SelectItem>
            <SelectItem value="likes">Likes</SelectItem>
            <SelectItem value="comments">Comments</SelectItem>
            <SelectItem value="engagement_rate">Engagement Rate</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {!accountId ? (
        <Card><CardContent className="py-12 text-center text-muted-foreground">{t.selectAccount}</CardContent></Card>
      ) : loading ? (
        <Card><CardContent className="py-12 flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></CardContent></Card>
      ) : !data?.data_available ? (
        <Card><CardContent className="py-12 text-center">
          <p className="text-muted-foreground">{t.noData}</p>
        </CardContent></Card>
      ) : (
        <>
          {/* Correlation coefficients */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[
              { key: "views", label: t.viewsCorr },
              { key: "likes", label: t.likesCorr },
              { key: "comments", label: t.commentsCorr },
              { key: "engagement_rate", label: t.erCorr },
            ].map(({ key, label }) => (
              <Card key={key} className={metric === key ? "ring-2 ring-primary/30" : ""}>
                <CardContent className="p-4 cursor-pointer" onClick={() => setMetric(key)}>
                  <p className="text-xs text-muted-foreground">{label}</p>
                  <div className="mt-1">
                    <CorrLabel value={data.correlation[key] || 0} t={t} />
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Scatter Plot */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">{t.scatterTitle}</CardTitle>
              <CardDescription>{metric.charAt(0).toUpperCase() + metric.slice(1)} vs {t.followerDelta}</CardDescription>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={400}>
                <ScatterChart margin={{ top: 10, right: 20, bottom: 20, left: 10 }}>
                  <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
                  <XAxis dataKey="x" type="number" tick={{ fontSize: 11 }} tickFormatter={formatNumber} name={metric} />
                  <YAxis dataKey="y" type="number" tick={{ fontSize: 11 }} name={t.followerDelta} />
                  <RechartsTooltip
                    content={({ active, payload }) => {
                      if (!active || !payload?.length) return null;
                      const d = payload[0]?.payload;
                      return (
                        <div className="bg-card border border-border rounded-lg p-3 shadow-lg text-sm max-w-[250px]">
                          <p className="font-medium truncate">{d.title}</p>
                          <p>{metric}: {formatNumber(d.x)}</p>
                          <p>{t.followerDelta}: {d.y >= 0 ? "+" : ""}{d.y.toLocaleString()}</p>
                          {d.category && <Badge variant="outline" className="text-xs mt-1">{d.category}</Badge>}
                        </div>
                      );
                    }}
                  />
                  <ReferenceLine y={0} stroke="hsl(var(--muted-foreground))" strokeDasharray="3 3" opacity={0.5} />
                  <Scatter data={scatterData} fill={COLORS[metric] || "#6b7280"} fillOpacity={0.6} />
                </ScatterChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
};

// ====== Main Page Component ======
const CorrelationPage = ({ lang = "ko" }) => {
  const t = T[lang] || T.ko;
  const { isAdmin } = useAuth();
  const [accounts, setAccounts] = useState([]);

  useEffect(() => {
    axios.get(`${API}/accounts`).then(({ data }) => setAccounts(data)).catch(() => {});
  }, []);

  return (
    <div className="space-y-6" data-testid="correlation-page">
      {/* Header */}
      <div className="flex items-center gap-3">
        <FlaskConical className="h-6 w-6 text-primary" />
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-xl font-bold">{t.title}</h1>
            <Badge variant="outline" className="bg-amber-500/10 text-amber-600 border-amber-500/30 text-xs font-medium">
              {t.beta}
            </Badge>
          </div>
          <p className="text-sm text-muted-foreground mt-0.5">{t.betaDesc}</p>
        </div>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="impact" className="space-y-4">
        <TabsList className="grid w-full grid-cols-5" data-testid="correlation-tabs">
          <TabsTrigger value="impact" data-testid="tab-impact">
            <TrendingUp className="h-4 w-4 mr-1.5 hidden sm:block" />{t.impactTab}
          </TabsTrigger>
          <TabsTrigger value="timeline" data-testid="tab-timeline">
            <BarChart3 className="h-4 w-4 mr-1.5 hidden sm:block" />{t.timelineTab}
          </TabsTrigger>
          <TabsTrigger value="category" data-testid="tab-category">
            <Tag className="h-4 w-4 mr-1.5 hidden sm:block" />{t.categoryTab}
          </TabsTrigger>
          <TabsTrigger value="correlation" data-testid="tab-correlation">
            <Users className="h-4 w-4 mr-1.5 hidden sm:block" />{t.correlationTab}
          </TabsTrigger>
          <TabsTrigger value="ai" data-testid="tab-ai">
            <Sparkles className="h-4 w-4 mr-1.5 hidden sm:block" />{t.aiTab}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="impact"><ImpactScoreTab lang={lang} accounts={accounts} /></TabsContent>
        <TabsContent value="timeline"><TimelineTab lang={lang} accounts={accounts} /></TabsContent>
        <TabsContent value="category"><CategoryTab lang={lang} accounts={accounts} /></TabsContent>
        <TabsContent value="correlation"><CorrelationTab lang={lang} accounts={accounts} /></TabsContent>
        <TabsContent value="ai"><AISummaryTab lang={lang} accounts={accounts} /></TabsContent>
      </Tabs>
    </div>
  );
};

export default CorrelationPage;
